import { CancelTokenSource } from "axios";

import { API } from "utils/api-config";

import { defaultErrorResponse } from "api/defaults";

import {
    CustomField,
    CustomFieldType,
    ExcludedLineType,
    InvoiceType,
    Separator,
    TtpMessageSourceField,
} from "store/environments/tenants/custom-fields/erp-types";
import { Tenant } from "store/environments/environment-types";

export const loadCustomFields = async (
    environmentId: string,
    tenantId: string,
    cancelTokenSource?: CancelTokenSource
): Promise<[Tenant<ReadonlyArray<CustomField>> | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.get(
        `environments/${environmentId}/tenants/${tenantId}/custom-fields/erp`,
        {
            cancelToken: cancelTokenSource?.token,
        }
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const updateCustomFields = async (
    environmentId: string,
    tenantId: string,
    payload: ReadonlyArray<CustomField>,
    cancelTokenSource?: CancelTokenSource
): Promise<[string | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.put(
        `environments/${environmentId}/tenants/${tenantId}/custom-fields/erp`,
        payload,
        {
            cancelToken: cancelTokenSource?.token,
        }
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const removeCustomFields = async (
    environmentId: string,
    tenantId: string,
    payload: ReadonlyArray<CustomField>,
    cancelTokenSource?: CancelTokenSource
): Promise<[string | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.request({
        method: "DELETE",
        url: `environments/${environmentId}/tenants/${tenantId}/custom-fields/erp`,
        cancelToken: cancelTokenSource?.token,
        data: payload,
    });

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const loadTargetTables = async (
    environmentId: string,
    tenantId: string,
    invoiceType: InvoiceType,
    cancelTokenSource?: CancelTokenSource
): Promise<[Tenant<ReadonlyArray<string>> | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.get(
        `environments/${environmentId}/tenants/${tenantId}/custom-fields/erp/target-tables/${invoiceType}`,
        {
            cancelToken: cancelTokenSource?.token,
        }
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const loadTargetFields = async (
    environmentId: string,
    tenantId: string,
    targetTable: string,
    cancelTokenSource?: CancelTokenSource
): Promise<[Tenant<ReadonlyArray<string>> | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.get(
        `environments/${environmentId}/tenants/${tenantId}/custom-fields/erp/target-fields/${targetTable}`,
        {
            cancelToken: cancelTokenSource?.token,
        }
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const loadLineTypes = async (
    environmentId: string,
    tenantId: string,
    cancelTokenSource?: CancelTokenSource
): Promise<[Tenant<ReadonlyArray<ExcludedLineType>> | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.get(
        `environments/${environmentId}/tenants/${tenantId}/custom-fields/erp/line-types`,
        {
            cancelToken: cancelTokenSource?.token,
        }
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const loadSeparators = async (
    environmentId: string,
    tenantId: string,
    cancelTokenSource?: CancelTokenSource
): Promise<[Tenant<ReadonlyArray<Separator>> | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.get(
        `environments/${environmentId}/tenants/${tenantId}/custom-fields/erp/separators`,
        {
            cancelToken: cancelTokenSource?.token,
        }
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const loadTtpMessageSourceFields = async (
    environmentId: string,
    tenantId: string,
    invoiceType: InvoiceType,
    cancelTokenSource?: CancelTokenSource
): Promise<[Tenant<ReadonlyArray<TtpMessageSourceField>> | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.get(
        `environments/${environmentId}/tenants/${tenantId}/custom-fields/erp/ttp-message-source-fields/${invoiceType}`,
        {
            cancelToken: cancelTokenSource?.token,
        }
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const loadTypes = async (
    environmentId: string,
    tenantId: string,
    cancelTokenSource?: CancelTokenSource
): Promise<[Tenant<ReadonlyArray<CustomFieldType>> | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.get(
        `environments/${environmentId}/tenants/${tenantId}/custom-fields/erp/types`,
        {
            cancelToken: cancelTokenSource?.token,
        }
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};
